import { useState, useEffect, useCallback, useMemo } from 'react';
import { transporterAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useTransporterDrivers = (filters = {}) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.assigned,
    filters.license_status
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchDrivers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      const response = await transporterAPI.getDrivers(params);
      setDrivers(response.data.results || response.data);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des chauffeurs:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des chauffeurs');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  const createDriver = useCallback(async (driverData) => {
    try {
      const response = await transporterAPI.createDriver(driverData);
      // Recharger la liste après création
      fetchDrivers();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la création du chauffeur:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la création du chauffeur' 
      };
    }
  }, [fetchDrivers]);

  const updateDriver = useCallback(async (driverId, driverData) => {
    try {
      const response = await transporterAPI.updateDriver(driverId, driverData);
      // Recharger la liste après modification
      fetchDrivers();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la modification du chauffeur:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la modification du chauffeur' 
      };
    }
  }, [fetchDrivers]);

  const deleteDriver = useCallback(async (driverId) => {
    try {
      await transporterAPI.deleteDriver(driverId);
      // Recharger la liste après suppression
      fetchDrivers();
      return { success: true };
    } catch (err) {
      console.error('Erreur lors de la suppression du chauffeur:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la suppression du chauffeur' 
      };
    }
  }, [fetchDrivers]);

  const assignVehicle = useCallback(async (driverId, vehicleId) => {
    try {
      const response = await transporterAPI.assignVehicleToDriver(driverId, vehicleId);
      // Recharger la liste après modification
      fetchDrivers();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de l\'assignation du véhicule:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de l\'assignation du véhicule' 
      };
    }
  }, [fetchDrivers]);

  const unassignVehicle = useCallback(async (driverId) => {
    try {
      const response = await transporterAPI.unassignVehicleFromDriver(driverId);
      // Recharger la liste après modification
      fetchDrivers();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la désassignation du véhicule:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la désassignation du véhicule' 
      };
    }
  }, [fetchDrivers]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  return {
    drivers,
    loading,
    error,
    pagination,
    fetchDrivers,
    createDriver,
    updateDriver,
    deleteDriver,
    assignVehicle,
    unassignVehicle,
  };
};
